import React, { useEffect, useState } from 'react';
import HighchartsReact from 'highcharts-react-official';
import { NoDataOptions } from 'highcharts';
import { observer } from 'mobx-react';
import { find, findIndex, findLast } from 'lodash';
import ReactDOMServer from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { GlobalToken, theme } from 'antd';

import { useStores } from '@/hooks/use-stores';

import { DynamicControllerContainer } from './styled';
import DynamicControllerTooltip from '../../MetricsTooltip/DynamicControllerTooltip';

const { useToken } = theme;

const getInitOptions = (t, token: GlobalToken) => ({
  chart: {
    type: 'xrange',
    backgroundColor: token.colorBgBase,
    zoomType: 'x',
    panning: {
      enabled: true,
    },
    panKey: 'alt',
    height: 60,
    margin: [0, 0, 0, 0],
    spacing: [0, 0, 0, 0],
    zooming: {
      resetButton: {
        theme: {
          style: {
            display: 'none',
          },
        },
      },
    },
    style: {
      fontFamily: 'Heebo',
    },
  },
  xAxis: {
    gridLineWidth: 1,
    maxPadding: 0,
    minPadding: 0,
  },
  yAxis: {
    visible: false,
  },
  plotOptions: {
    series: {
      borderColor: 'transparent',
      borderRadius: 0,
      pointWidth: 12,
      turboThreshold: 10000,
    },
    xrange: {
      pointPlacement: 'on',
    },
  },
  lang: { noData: '' },
  title: {
    text: t('Dynamic Controller'),
    align: 'left',
    x: 10,
    y: 45,
    style: {
      color: token.colorBorder,
      fontSize: 10,
      margin: 0,
      fontWeight: '500',
    },
  },
  noData: '' as NoDataOptions,
  credits: {
    enabled: false,
  },
  tooltip: {
    useHTML: true,
    borderColor: undefined,
    borderWidth: 0,
    borderRadius: 0,
    backgroundColor: undefined,
    hideDelay: 100,
    positioner: (_, __, point) => {
      const mouseGap = 12;
      const tooltipWidth = 430;
      const isRightSide = point.plotX > window.innerWidth / 2;

      return {
        y: point.plotY,
        x: point.plotX + (isRightSide ? (tooltipWidth + mouseGap) * -1 : mouseGap),
      };
    },
  },
  legend: {
    enabled: false,
  },
});

const getSameControllerStatusStartDate = (series, baseCompareObject) =>
  findLast(series, (currentLog, index: number) =>
    currentLog?.x <= baseCompareObject?.x && series?.[index - 1]?.name
      ? currentLog?.name !== series?.[index - 1]?.name
      : null,
  )?.x ?? series?.[0]?.x;

const getSameControllerStatusEndDate = (series, baseCompareObject) =>
  find(series, (currentLog, index: number) =>
    currentLog?.x2 >= baseCompareObject?.x2 && series?.[index + 1]?.name
      ? currentLog?.name !== series?.[index + 1]?.name
      : null,
  )?.x2;

const DynamicController = observer(
  ({ highcharts, isSecondPeriod }: { syncronizeCrossHairs; highcharts; isSecondPeriod? }) => {
    const { t } = useTranslation();
    const { token } = useToken();
    const initOptions = getInitOptions(t, token);
    const [options, setOptions] = useState<unknown>(initOptions);
    const { investigationStore } = useStores();
    const dates = isSecondPeriod ? investigationStore.dates2 : investigationStore.dates1;
    const data = isSecondPeriod ? investigationStore.data2 : investigationStore.data1;
    const series = isSecondPeriod
      ? investigationStore.proactiveEngineLogsSeries2
      : investigationStore.proactiveEngineLogsSeries;

    useEffect(() => {
      if (!dates) {
        setOptions(initOptions);
        return;
      }

      const newOptions = {
        ...initOptions,
        title: {
          ...initOptions.title,
          ...(isSecondPeriod && { text: undefined }),
        },
        tooltip: isSecondPeriod
          ? { enabled: false }
          : {
              ...initOptions.tooltip,
              formatter: function () {
                const secondPeriodInfo =
                  investigationStore.proactiveEngineLogsSeries2?.[findIndex(series, { x: this.x })];
                return ReactDOMServer.renderToString(
                  <DynamicControllerTooltip
                    currentPeriodInfo={{
                      point: this.x,
                      name: this.key,
                      sameControllerStatusStartDate: getSameControllerStatusStartDate(series, this),
                      sameControllerStatusEndDate: getSameControllerStatusEndDate(series, this),
                    }}
                    secondPeriodInfo={
                      investigationStore.isCompareMode &&
                      !!investigationStore.proactiveEngineLogsSeries2?.length && {
                        point: secondPeriodInfo?.x,
                        name: secondPeriodInfo?.name,
                        sameControllerStatusStartDate: getSameControllerStatusStartDate(
                          investigationStore.proactiveEngineLogsSeries2,
                          secondPeriodInfo,
                        ),
                        sameControllerStatusEndDate: getSameControllerStatusEndDate(
                          investigationStore.proactiveEngineLogsSeries2,
                          secondPeriodInfo,
                        ),
                      }
                    }
                    token={token}
                  />,
                );
              },
            },
        series: [
          {
            name: t('dynamicControl'),
            data: series,
          },
        ],
        chart: {
          ...initOptions.chart,
          ...(isSecondPeriod && {
            height: 18,
          }),
          // events: {
          //   load: chart => syncronizeCrossHairs(chart?.target),
          // },
        },
        xAxis: {
          ...initOptions.xAxis,
          events: {
            afterSetExtremes: function (e) {
              if (e?.trigger === 'zoom') {
                highcharts.charts?.forEach(chart => {
                  if (chart) {
                    if (chart.options?.chart?.type === 'line') {
                      chart.showResetZoom();
                    }
                    chart?.xAxis?.forEach(
                      xAxis => xAxis && xAxis?.setExtremes(this.chart.xAxis[0].min, this.chart.xAxis[0].max),
                    );
                  }
                });
              }
            },
          },
        },
      };
      setOptions(newOptions);
    }, [
      investigationStore.devicesMetricsAggregationDataQueryParams,
      investigationStore.isLoading,
      investigationStore.selectedMetrics,
      investigationStore.selectedPressureDrops,
      data,
      dates,
    ]);

    return series?.length ? (
      <DynamicControllerContainer>
        <div className="chart">
          <HighchartsReact highcharts={highcharts} options={options} />
        </div>
      </DynamicControllerContainer>
    ) : null;
  },
);

export default DynamicController;
